/**
 * The mark scheme as its own document, laid out as official schemes are: a table of
 * Question | Answer | Marks, one row per question or part, and the total at the foot.
 *
 * It exists for print — a teacher or parent marks a script with it beside them — so it is
 * drawn only while the paper is printed as "Mark scheme", and hidden on screen even then.
 */

import type { ReactNode } from "react";
import { questionMarks } from "./marks";
import type { Paper } from "./types";
import { CoverTag, type PaperIdentity } from "./PrintTag";
import { RichText } from "../text/RichText";

export interface SchemeRow {
  /** "01", "01(a)": as the paper prints it. */
  label: string;
  /** The question's topic, on its first row only. */
  topic: string;
  answer: string;
  points: string[];
  marks: number;
  /** A multiple-choice question: its answer is the correct option. */
  option: boolean;
}

const two = (n: number | string) => String(n).padStart(2, "0");

export function schemeRows(paper: Paper): SchemeRow[] {
  return paper.questions.flatMap((q) => {
    if (q.parts.length === 0) {
      return [
        {
          label: two(q.number),
          topic: q.topic,
          answer: q.answer,
          points: q.marking_points,
          marks: q.marks,
          option: q.type === "multiple_choice",
        },
      ];
    }
    return q.parts.map((p, i) => ({
      label: `${two(q.number)}${p.label}`,
      topic: i === 0 ? q.topic : "",
      answer: p.answer,
      points: p.marking_points,
      marks: p.marks,
      option: false,
    }));
  });
}

function Award({ row }: { row: SchemeRow }) {
  if (row.option) {
    return (
      <p className="scheme-sheet__answer">
        Correct option: <strong>{row.answer}</strong>
      </p>
    );
  }
  const points = row.points.filter((p) => p.trim() !== "");
  return (
    <>
      {row.answer && (
        <div className="scheme-sheet__answer">
          <RichText text={row.answer} block />
        </div>
      )}
      {row.marks > 0 && points.length > row.marks && (
        <div className="scheme-sheet__any">Any {row.marks} from:</div>
      )}
      {points.length > 0 && (
        <ul className="scheme-sheet__points">
          {points.map((point, i) => (
            <li key={`${i}-${point}`}>
              <RichText text={point} />
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

export function MarkSchemeSheet({
  paper,
  board,
  disclaimer,
  title: Title = "h2",
  identity,
}: {
  paper: Paper;
  board?: string;
  disclaimer: ReactNode;
  title?: "h1" | "h2";
  /** Paper N and its Ref: the scheme matches its paper at a glance (spec 8). */
  identity?: PaperIdentity;
}) {
  const rows = schemeRows(paper);
  const total = paper.questions.reduce((n, q) => n + questionMarks(q), 0);
  return (
    <section className="scheme-sheet" aria-label="Mark scheme" data-testid="scheme-sheet">
      <header className="scheme-sheet__head">
        {identity && <CoverTag identity={identity} kind="Mark scheme" />}
        <div className="cover__board">{board ?? "Practice paper"}</div>
        <Title className="scheme-sheet__title">
          {identity
            ? `Mark scheme — Paper ${identity.number} (Ref ${identity.ref})`
            : `${paper.title} — Mark scheme`}
        </Title>
        {identity && (
          <div className="scheme-sheet__for">
            {paper.title} · for the question paper with the same Ref
          </div>
        )}
        <div className="scheme-sheet__meta">
          Total marks: <strong>{total}</strong>
        </div>
      </header>
      <p className="scheme-sheet__rubric">
        Award one mark for each point. Accept equivalent wording.
      </p>
      <table className="scheme-sheet__table">
        <thead>
          <tr>
            <th scope="col">Question</th>
            <th scope="col">Answer</th>
            <th scope="col">Marks</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.label}>
              <th scope="row">{row.label}</th>
              <td>
                {row.topic && <div className="scheme-sheet__topic">{row.topic}</div>}
                <Award row={row} />
              </td>
              <td>{row.marks}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th scope="row" colSpan={2}>
              Total
            </th>
            <td>{total}</td>
          </tr>
        </tfoot>
      </table>
      <p className="paper__disclaimer">{disclaimer}</p>
    </section>
  );
}
